import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';

import { adminService } from '../../../services/adminService';
import PromptFieldManager from './PromptFieldManager';
import PromptTemplateManager from './PromptTemplateManager';

const RelationshipMatrix = ({ useCases, aiTools, onRefresh }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const isLinked = (useCaseId, aiToolId) => {
    const useCase = useCases?.find(uc => uc?.id === useCaseId);
    return useCase?.aiTools?.some(tool => tool?.id === aiToolId) || false;
  };

  const handleToggleLink = async (useCaseId, aiToolId) => {
    try {
      setLoading(true);
      setError(null);

      if (isLinked(useCaseId, aiToolId)) {
        await adminService?.unlinkUseCaseFromAITool(useCaseId, aiToolId);
      } else {
        await adminService?.linkUseCaseToAITool(useCaseId, aiToolId);
      }

      await onRefresh();
    } catch (err) {
      console.error('Error toggling link:', err);
      setError(err?.message || 'Failed to update relationship');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-6">
      <div className="mb-6">
        <h2 className="text-xl font-semibold text-foreground mb-2">Use Case & AI Tool Relationships</h2>
        <p className="text-sm text-muted-foreground">
          Manage which AI tools are available for each use case and configure their prompt fields
        </p>
      </div>

      {error && (
        <div className="mb-4 bg-destructive/10 border border-destructive rounded-lg p-3">
          <p className="text-sm text-destructive">{error}</p>
        </div>
      )}

      {/* Desktop View */}
      <div className="hidden md:block overflow-x-auto">
        <table className="w-full border-collapse">
          <thead>
            <tr>
              <th className="border border-border bg-muted p-3 text-left font-semibold text-foreground">
                Use Case \ AI Tool
              </th>
              {aiTools?.map((tool) => (
                <th
                  key={tool?.id}
                  className="border border-border bg-muted p-3 text-center font-semibold text-foreground min-w-[120px]"
                >
                  <div className="flex flex-col items-center gap-1">
                    <span className="text-sm">{tool?.name}</span>
                    <span className="text-xs text-muted-foreground font-normal">{tool?.value}</span>
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {useCases?.map((useCase) => (
              <tr key={useCase?.id} className="border-b border-border hover:bg-muted/50 transition-colors">
                <td className="px-4 py-3 font-medium text-foreground">
                  <div className="flex items-center gap-2">
                    {useCase?.icon && <Icon name={useCase?.icon} size={18} />}
                    <span>{useCase?.name}</span>
                  </div>
                </td>
                {aiTools?.map((tool) => {
                  const isLinked = useCase?.aiTools?.some(t => t?.id === tool?.id);
                  return (
                    <td key={tool?.id} className="px-4 py-3 text-center">
                      <div className="flex flex-col items-center gap-2">
                        <button
                          onClick={() => handleToggleLink(useCase?.id, tool?.id)}
                          className={`
                            w-8 h-8 rounded-full flex items-center justify-center transition-all
                            ${isLinked
                              ? 'bg-primary text-primary-foreground hover:bg-primary/80'
                              : 'bg-muted text-muted-foreground hover:bg-muted/80'
                            }
                          `}
                          title={isLinked ? 'Click to unlink' : 'Click to link'}
                        >
                          <Icon name={isLinked ? 'Check' : 'X'} size={16} />
                        </button>
                        {isLinked && (
                          <div className="flex flex-col gap-1">
                            <PromptFieldManager
                              useCaseId={useCase?.id}
                              aiToolId={tool?.id}
                              useCaseName={useCase?.name}
                              aiToolName={tool?.name}
                              onRefresh={onRefresh}
                            />
                            <PromptTemplateManager
                              useCaseId={useCase?.id}
                              aiToolId={tool?.id}
                              useCaseName={useCase?.name}
                              aiToolName={tool?.name}
                              onRefresh={onRefresh}
                            />
                          </div>
                        )}
                      </div>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile View */}
      <div className="md:hidden space-y-4">
        {useCases?.map((useCase) => (
          <div key={useCase?.id} className="border border-border rounded-lg p-4">
            <div className="flex items-center gap-2 mb-3">
              {useCase?.icon && <Icon name={useCase?.icon} size={20} className="text-primary" />}
              <h3 className="font-semibold text-foreground">{useCase?.name}</h3>
            </div>
            <div className="space-y-2">
              {aiTools?.map((tool) => (
                <div
                  key={tool?.id}
                  className="flex items-center justify-between p-2 bg-muted rounded"
                >
                  <span className="text-sm text-foreground">{tool?.name}</span>
                  <button
                    onClick={() => handleToggleLink(useCase?.id, tool?.id)}
                    disabled={loading}
                    className={`px-3 py-1 rounded text-sm transition-all ${
                      isLinked(useCase?.id, tool?.id)
                        ? 'bg-primary text-primary-foreground'
                        : 'bg-background text-muted-foreground border border-border'
                    } disabled:opacity-50`}
                  >
                    {isLinked(useCase?.id, tool?.id) ? 'Linked' : 'Link'}
                  </button>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      {(useCases?.length === 0 || aiTools?.length === 0) && (
        <div className="text-center py-12">
          <Icon name="Network" size={48} className="text-muted-foreground mx-auto mb-4" />
          <p className="text-muted-foreground">
            {useCases?.length === 0
              ? 'No use cases found. Add use cases first.' :'No AI tools found. Add AI tools first.'}
          </p>
        </div>
      )}
    </div>
  );
};

export default RelationshipMatrix;